import type { TradersWidgetModel } from '.';
import { makeHeaderElements } from '../WidgetModel';
import type { WidgetHeaderElement } from '../WidgetModel';
import { COMMON_HEADER_ELEMENTS } from '../WidgetModel/impl';
import type { TradersWidgetState } from './TradersSelfOwnedModel';
import { TradersSelfOwnedModel, tradersWidgetStateSchema } from './TradersSelfOwnedModel';
import type { ReadonlySignal } from '@preact/signals-react';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import type { ColumnTemplate } from '@thinkalpha/ui-types/contracts/column-template';
import { AGGREGATED_RISK_WIDGET_CONFIG_BY_KIND } from 'src/features/aggregated-risk/config';
import type { AggregatedRiskWidgetConfig } from 'src/features/aggregated-risk/config';
import { inject, injectable } from 'src/features/ioc';
import type { AggregatedRiskWidgetRuntimeState } from 'src/models/AggregatedRiskWidgetModel';
import { AggregatedRiskWidgetTableModel } from 'src/models/AggregatedRiskWidgetModel/AggregatedRiskWidgetTableModel';
import type { TableModel } from 'src/models/TableModel';
import { getAggregatedRiskTableQueryOptions } from 'src/queries/aggregatedRiskTables';
import type { ReactWidgetContainer } from 'src/ReactContainer';
import { decodeSelfOwnedWidgetState } from 'src/services/WorkspaceService/selfOwnedWidget';
import type { GetInitialData, WidgetId } from 'src/services/WorkspaceService/selfOwnedWidget';
import type { WidgetContainerController } from 'src/services/WorkspaceService/WidgetContainer/WidgetContainer';
import type { ReactBindings } from 'src/types/bindings';

export function createTradersWidgetHeaderElements(config: AggregatedRiskWidgetConfig): WidgetHeaderElement[] {
    return makeHeaderElements<TradersWidgetModel['eventBus']>([
        ...COMMON_HEADER_ELEMENTS,
        { type: 'stickyFilter', position: 40, priority: 80 },
        {
            type: 'columnTemplateMenu',
            position: 70,
            priority: 20,
            columnTemplateKey: config.templateKey,
            alwaysUseGridSettings: true,
        },
        { type: 'tableRowCount', position: 75, priority: 11 },
        { type: 'connectionStatus', position: 76, priority: 12, overflowPosition: 5 },
    ]);
}

@injectable()
export class TradersWidgetModelImpl implements TradersWidgetModel {
    static readonly stateSchema = tradersWidgetStateSchema;

    static defaultState(): TradersWidgetState {
        return { columnTemplate: null, filter: null };
    }

    static fromJSON(container: ReactWidgetContainer): TradersWidgetModelImpl {
        const getInitialData = container.get('GetInitialData') as GetInitialData<unknown>;
        const raw = getInitialData();
        const validated = decodeSelfOwnedWidgetState(TradersWidgetModelImpl.stateSchema, raw);
        container.bind('InitialWidgetState').toConstantValue(validated);
        return container.get('TradersWidgetModel') as TradersWidgetModelImpl;
    }

    readonly #model: TradersSelfOwnedModel;
    readonly #table: TableModel;
    readonly #tableModel: AggregatedRiskWidgetTableModel;
    readonly #disposableStack = new DisposableStack();
    readonly headerElements: WidgetHeaderElement[];

    constructor(
        @inject('WidgetId') tabId: WidgetId,
        @inject('WidgetContainerController') container: WidgetContainerController,
        @inject('InitialWidgetState') initialState: TradersWidgetState,
        @inject('Logger') logger: Logger,
        @inject('SlugMapService') slugMapService: ReactBindings['SlugMapService'],
        @inject('SlugMapServiceProvider') slugMapServiceProvider: ReactBindings['SlugMapServiceProvider'],
        @inject('QueryClient') queryClient: ReactBindings['QueryClient'],
        @inject('TableModel') table: TableModel,
    ) {
        const persisted = initialState;
        const config = AGGREGATED_RISK_WIDGET_CONFIG_BY_KIND.traders;

        this.#table = this.#disposableStack.use(table);

        this.#model = this.#disposableStack.use(
            new TradersSelfOwnedModel({
                container,
                tabId,
                persisted,
                logger,
                queryClient,
                slugMapService,
                table: this.#table,
            }),
        );

        this.#tableModel = this.#disposableStack.use(
            new AggregatedRiskWidgetTableModel({
                columnTemplate: this.#model.columnTemplate,
                columnTemplateError: this.#model.columnTemplateError,
                config,
                devtoolsName: 'Traders widget',
                groupByIndicatorIds: this.#model.groupByIndicatorIds,
                filters: this.#model.tableFilters,
                slugMapServiceProvider,
                table: this.#table,
                tableKind: 'traders',
                tableQueryOptionsFactory: getAggregatedRiskTableQueryOptions,
                widgetId: tabId,
            }),
        );

        this.headerElements = createTradersWidgetHeaderElements(config);
    }

    get type(): 'traders' {
        return 'traders';
    }

    get dirty(): ReadonlySignal<boolean> {
        return this.#model.dirty;
    }

    get state(): ReadonlySignal<AggregatedRiskWidgetRuntimeState> {
        return this.#tableModel.state;
    }

    get columnTemplate(): ReadonlySignal<ColumnTemplate | null> {
        return this.#model.columnTemplate;
    }

    get groupByIndicatorIds(): ReadonlySignal<readonly string[]> {
        return this.#model.groupByIndicatorIds;
    }

    get columnTemplateError(): ReadonlySignal<Error | null> {
        return this.#model.columnTemplateError;
    }

    get eventBus(): TradersWidgetModel['eventBus'] {
        return this.#model.eventBus;
    }

    get table(): TableModel {
        return this.#table;
    }

    toJSON(): TradersWidgetState {
        return this.#model.toJSON();
    }

    markClean(): void {
        this.#model.markClean();
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }
}
